import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { defaultKunde, Kunde, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    Auftrag,
    addAuftrag,
    updateAuftrag,
    useAuftrag,
    sendAuftrag,
    copyAuftrag,
    getDefaultAuftrag,
    deleteAuftrag,
    addAuftragFromAuftrag,
    finalizeAuftrag,
    useAuftragList,
} from 'api/auftrag';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { AuftragStatus } from 'formComponents/lookups/AuftragStatus';
import { useHistory } from 'react-router-dom';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney, formatPercent } from 'utils/formatter';
import { AuftragPosition, useAuftragPositionList } from 'api/auftragPosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { SendMail } from 'components/SendMail/SendMail';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { ZusatzadresseSelect } from 'components/Kunde/Zusatzadresse/ZusatzadresseSelect';
import { ZusatzadresseTyp } from 'api/zusatzadresse';
import { addLieferungFromAuftrag, useLieferungList } from 'api/lieferung';
import { AuftragStatusLabel, getAuftragLabel, LieferungStatusLabel, RechnungStatusLabel } from 'utils/labels';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { addBestellungFromAuftrag, useBestellungList } from 'api/bestellung';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import useArtikelCalculator from 'hooks/useArtikelCalculator';
import { addRechnungFromAuftrag, useRechnungList } from 'api/rechnung';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { getArtikelDetail } from 'api/artikelDetail';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { ChevronDownIcon, ChevronUpIcon } from '@fluentui/react-icons';
import { SmRefType } from 'api/attachment';
import { useTerminList } from 'api/termin';
import { addServiceFromAuftrag, useServiceList } from 'api/service';
import { AuftragPositionen } from './AuftragPositionen';
import { AuftragPositionenSelect } from './AuftragPositionenSelect';
import { RechnungPositionen } from '../Rechnung/RechnungPositionen';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { AddAttachments } from '../History/AddAttachments';

const styles = mergeStyleSets({
    linkBoxTitle: {
        fontWeight: 600,
        marginTop: 5,
    },
    rechnungLine: {
        display: 'grid',
        gridTemplateColumns: '100px 100px 100px 100px 100px 100px max-content',
        gridColumnGap: 10,
    },
    rechnungPositionList: {
        width: '100%',
    },
    teilrechnung: {
        marginTop: 10,
        display: 'grid',
        gridTemplateColumns: 'max-content 100px max-content',
        gridColumnGap: 10,
    },
    checkbox: {
        marginTop: 8,
        fontWeight: 700,
        marginLeft: 5,
        marginBottom: 20,
    },
    teilrechnungPercent: {},
    percentSign: {
        marginTop: 6,
    },
});

export interface AuftragEditData {
    kNr?: number;
    auNr: string;
}

interface AuftragEditProps {
    defaultKNr?: number;
    auNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction =
    | 'reopen'
    | 'print'
    | 'printOnBriefpapier'
    | 'send'
    | 'copy'
    | 'createLieferung'
    | 'createRechnung'
    | 'createAbrufAuftrag'
    | 'createBestellung'
    | 'createService'
    | 'finalize'
    | 'delete';

const allowedStatus = [0, 1, 9, 4];

export const AuftragEdit: React.FC<AuftragEditProps> = ({ defaultKNr, auNr, onClose, onReopen }) => {
    const history = useHistory();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [auftrag, setAuftrag] = React.useState<Auftrag | null>(getDefaultAuftrag(defaultKNr));
    const dbAuftrag = useAuftrag(
        {
            nr: auNr,
        },
        {
            enabled: auNr !== 'neu',
            onSuccess: (loadedAuftrag) => {
                setAuftrag(cloneDeep(loadedAuftrag));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: auftrag?.kNr,
            withZusatzadresseList: true,
            withAttachmentList: true,
        },
        {
            enabled: auftrag?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (auNr === 'neu' && auftrag) {
                    const lieferAdr = newKunde?.zusatzadresseList?.find((za) => za.typ === 'L' && za.std && !za.archiv);
                    const rechnungsAdr = newKunde?.zusatzadresseList?.find(
                        (za) => za.typ === 'R' && za.std && !za.archiv,
                    );

                    setAuftrag({
                        ...auftrag,
                        betreuer: newKunde?.betreuer || 0,
                        telBetreuer: newKunde?.telBetreuer || 0,
                        zahlBed: newKunde?.zahlZiel || auftrag.zahlBed,
                        lieferAdrNr: lieferAdr?.nr || 0,
                        lieferAdr,
                        rechnungsAdrNr: rechnungsAdr?.nr || 0,
                        rechnungsAdr,
                        ara: newKunde?.ara || 0,
                    });
                }
            },
        },
    );

    const auftragPositionList = useAuftragPositionList(
        {
            auNr,
        },
        { enabled: auNr !== 'neu' },
    );

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];
        if (auftrag && auftragPositionList.data) {
            (auftragPositionList.data || []).forEach((p) => {
                if (p.artikelGruppeNr && p.artikelNr) {
                    list.push(auftrag.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                }
            });
        }

        return list;
    }, [auftrag, auftragPositionList]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const bestellungList = useBestellungList(
        {
            auNr,
            withKunde: false,
        },
        { enabled: auNr !== 'neu' },
    );

    const serviceList = useServiceList(
        {
            auNr,
            withKunde: false,
        },
        { enabled: auNr !== 'neu' },
    );

    const lieferungList = useLieferungList(
        {
            auNr,
            withKunde: false,
        },
        { enabled: auNr !== 'neu' },
    );

    const rechnungList = useRechnungList(
        {
            auNr,
            withKunde: false,
        },
        { enabled: auNr !== 'neu' },
    );

    const rechnungPositionListFilter = React.useMemo<RechnungPositionFilter>(
        () => ({
            rNrList: (rechnungList.data || []).map((r) => r.nr),
        }),
        [rechnungList.data],
    );

    const rechnungPositionList = useRechnungPositionList(rechnungPositionListFilter, {
        enabled: auNr !== 'neu' && !!rechnungList.data,
    });

    const abrufAuftragList = useAuftragList(
        {
            abrufZu: auNr,
            withKunde: false,
        },
        { enabled: auNr !== 'neu' && auftrag?.rahmen },
    );

    // const [kunde, setKunde] = React.useState<Kunde | null>(null);
    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [showCreateLieferung, setShowCreateLieferung] = React.useState(false);
    const [createLieferungError, setCreateLieferungError] = React.useState<string | null>(null);
    const [showCreateRechnung, setShowCreateRechnung] = React.useState(false);
    const [showCreateAbrufAuftrag, setShowCreateAbrufAuftrag] = React.useState(false);
    const [showCreateBestellung, setShowCreateBestellung] = React.useState(false);
    const [showCreateService, setShowCreateService] = React.useState(false);
    const [showFinalize, setShowFinalize] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [transferKundeNr, setTransferKundeNr] = React.useState<number | null>(null);
    const [transferKunde, setTransferKunde] = React.useState<Kunde | null>(null);
    const [transferPositionen, setTransferPositionen] = React.useState<AuftragPosition[] | null>(null);
    const [teilrechnung, setTeilRechnung] = React.useState({
        isTeilRechnung: false,
        trProz: 100,
        trProzCalc: 100,
    });
    const [showTeilrechnungDetails, setShowTeilrechnungDetails] = React.useState<{ [key: string]: boolean }>({});
    const [finalizeAuftragWithRechnung, setfinalizeAuftragWithRechnung] = React.useState(true);
    const [setOffenToZero, setSetOffenToZero] = React.useState(true);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    const artikelCalculator = useArtikelCalculator();

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            setKunde(kundeList.data[0]);
            if (auNr === 'neu' && auftrag) {
                const lieferAdr = kundeList?.data[0]?.zusatzadresseList?.find((za) =>
                za.typ === 'L' && za.std && !za.archiv);
                const rechnungsAdr = kundeList?.data[0]?.zusatzadresseList?.find((za) =>
                za.typ === 'R' && za.std && !za.archiv);

                setAuftrag({
                    ...auftrag,
                    betreuer: kundeList.data[0]?.betreuer || 0,
                    telBetreuer: kundeList?.data[0]?.telBetreuer || 0,
                    zahlBed: kundeList?.data[0]?.zahlZiel || auftrag.zahlBed,
                    lieferAdrNr: lieferAdr?.nr || 0,
                    lieferAdr,
                    rechnungsAdrNr: rechnungsAdr?.nr || 0,
                    rechnungsAdr,
                    ara: kundeList.data[0]?.ara || 0,
                });
            }
        }
    }, [kunde, kundeList, auNr, auftrag]);

    React.useEffect(() => {
        if (!auftragList.isLoading && !auftragList.isFetching && auftrag === null) {
            if (auftragList.data?.length === 1) {
                setAuftrag(cloneDeep(dbAuftrag));
                setSavingState(SavingState.notChanged);
            } else {
                setAuftrag(getDefaultAuftrag(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [auftrag, auftragList, defaultKNr, auth, kundeList]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbAuftrag.isLoading &&
            !dbAuftrag.isFetching &&
            auftrag !== null
        ) {
            if (!isEqual(dbAuftrag.data, auftrag)) {
                setSavingState(SavingState.notSaved);
                logDifference('auftrag', auftrag, dbAuftrag);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbAuftrag, auftrag]);

    const readOnly = React.useMemo(() => {
        return auftrag?.status !== 0;
    }, [auftrag?.status]);

    const readOnlyPositionen = React.useMemo(() => {
        return auftrag?.status !== 0 && auftrag?.status !== 1;
    }, [auftrag?.status]);

    const validate = React.useCallback((): boolean => {
        if (auftrag?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [auftrag, validationError]);

    const getDistinctAuftragPositionen = React.useCallback(() => {
        const distinctAuftragPositionen: AuftragPosition[] = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionList?.data || []) {
            const existing = distinctAuftragPositionen.find(
                (dap) =>
                    dap.artikelGruppeNr === auftragPosition.artikelGruppeNr &&
                    dap.artikelNr === auftragPosition.artikelNr,
            );
            if (existing) {
                existing.offen += auftragPosition.offen;
                existing.posMenge += auftragPosition.posMenge;
            } else {
                distinctAuftragPositionen.push({ ...auftragPosition });
            }
        }

        return distinctAuftragPositionen;
    }, [auftragPositionList?.data]);

    const handleButtonAction = React.useCallback(
        async (action?: ButtonAction) => {
            if (!auftrag?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(auftrag.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('auftrag/pdf') + '?nr=' + auftrag.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('auftrag/pdf') + '?nr=' + auftrag.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                case 'createLieferung':
                    {
                        const newTransferPositionen = await Promise.all(
                            getDistinctAuftragPositionen().map(async (ap) => {
                                const artikelDetail =
                                    ap.artikelGruppeNr && ap.artikelNr
                                        ? await getArtikelDetail({
                                              artikelGruppeNr: ap.artikelGruppeNr,
                                              artikelNr: ap.artikelNr,
                                          })
                                        : null;

                                return {
                                    ...ap,
                                    transfer: ap.offen > 0,
                                    transferMenge:
                                        ap.lArt === 'L' ? Math.min(ap.offen, artikelDetail?.bestand || 0) : ap.offen,
                                    transferBestand: artikelDetail?.bestand || 0,
                                    transferLArt: ap.lArt,
                                };
                            }),
                        );

                        setTransferPositionen(newTransferPositionen);

                        setShowCreateLieferung(true);
                    }
                    break;
                case 'createRechnung':
                    setTransferPositionen(
                        (auftragPositionList?.data || []).map((ap) => ({
                            ...ap,
                            transfer: true,
                            transferMenge: ap.posMenge,
                        })),
                    );
                    setShowCreateRechnung(true);
                    break;
                case 'createAbrufAuftrag':
                    {
                        setTransferKundeNr(auftrag?.kNr || null);
                        setTransferKunde(kunde.data || null);

                        const newTransferPositionen = await Promise.all(
                            (auftragPositionList?.data || []).map(async (ap) => {
                                const artikelDetail = await getArtikelDetail({
                                    artikelGruppeNr: ap.artikelGruppeNr,
                                    artikelNr: ap.artikelNr,
                                });

                                return {
                                    ...ap,
                                    transfer: ap.offen > 0,
                                    transferMenge: ap.offen,
                                    transferBestand: artikelDetail.bestand,
                                    transferLArt: ap.lArt,
                                };
                            }),
                        );

                        setTransferPositionen(newTransferPositionen);
                        setShowCreateAbrufAuftrag(true);
                    }
                    break;
                case 'createBestellung':
                    {
                        const newTransferPositionen = await Promise.all(
                            getDistinctAuftragPositionen().map(async (ap) => {
                                const artikelDetail = await getArtikelDetail({
                                    artikelGruppeNr: ap.artikelGruppeNr,
                                    artikelNr: ap.artikelNr,
                                });

                                return {
                                    ...ap,
                                    transfer: false,
                                    transferMenge: ap.offen,
                                    transferBestand: artikelDetail.bestand,
                                    // transferLArt: artikelDetail?.lagerIntNr === 0 ? 'D' : 'L',
                                    transferLArt: ap.lArt,
                                };
                            }),
                        );

                        setTransferPositionen(newTransferPositionen);
                        setShowCreateBestellung(true);
                    }
                    break;
                case 'createService':
                    {
                        const newTransferPositionen = await Promise.all(
                            getDistinctAuftragPositionen().map(async (ap) => {
                                const artikelDetail = await getArtikelDetail({
                                    artikelGruppeNr: ap.artikelGruppeNr,
                                    artikelNr: ap.artikelNr,
                                });

                                return {
                                    ...ap,
                                    transfer: false,
                                    transferMenge: ap.offen,
                                    transferBestand: artikelDetail.bestand,
                                    // transferLArt: artikelDetail?.lagerIntNr === 0 ? 'D' : 'L',
                                    transferLArt: ap.lArt,
                                };
                            }),
                        );

                        setTransferPositionen(newTransferPositionen);
                        setShowCreateService(true);
                    }
                    break;
                case 'finalize':
                    setShowFinalize(true);
                    break;
                default:
                    break;
            }
        },
        [kunde, auftrag, onReopen, auftragPositionList?.data, getDistinctAuftragPositionen],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!auftrag) {
                return;
            }

            const updatedAuftrag: Auftrag = status === undefined ? auftrag : { ...auftrag, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (auNr === 'neu') {
                        addAuftrag(updatedAuftrag)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateAuftrag(updatedAuftrag)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbAuftrag.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(auftrag.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbAuftrag, auftrag, auNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: AuftragStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => (handleButtonAction('print').then() as never) as void,
            },
        ];

        if (auNr !== 'neu' && auftrag?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => (handleButtonAction('delete').then() as never) as void,
            });
        }

        return { items };
    }, [auNr, auftrag?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Auftrag, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(auftrag?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof Auftrag,
        label: string,
        options: FormDecimalFieldOptions = {},
        ignoreReadOnly = false,
    ) => (
        <FormDecimalField
            value={(auftrag?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || (readOnly && !ignoreReadOnly) }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Auftrag, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={auftrag?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Auftrag, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={auftrag?.[column] as boolean}
            onChange={(event, checked) => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderMa = (column: keyof Auftrag, label: string) => (
        <BetreuerById
            value={auftrag?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Auftrag,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={auftrag?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (column: keyof Auftrag, label: string, required = false, defaultNow = false) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(auftrag?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (auftrag) {
                        setAuftrag({
                            ...auftrag,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderAuftragStatus = () => (
        <AuftragStatus
            value={auftrag?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Kunde"
            kNr={auftrag?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly /* || auNr !== 'neu' */}
            onChange={(newKNr): void => {
                if (auftrag && auftrag.kNr !== newKNr) {
                    setAuftrag({
                        ...auftrag,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                        lieferAdrNr: 0,
                        lieferAdr: undefined,
                        rechnungsAdrNr: 0,
                        rechnungsAdr: undefined,
                    });
                }
            }}
        />
    );

    const renderAddKundeSelect = (
        column: 'lieferKNr' | 'rechnungsKNr',
        subRecord: 'lieferKunde' | 'rechnungsKunde',
        label: string,
    ) => (
        <KundeSelect
            label={label}
            kNr={auftrag?.[column] || 0}
            name={auftrag?.[subRecord]?.name1 || ''}
            allowEmpty
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newKNr, newKunde): void => {
                if (auftrag && auftrag.kNr !== newKNr) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newKNr,
                        [subRecord]: newKunde,
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={auftrag?.kNr}
            kaNr={auftrag?.kaNr}
            kaName={auftrag?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderZusatzadresse = (
        column: 'lieferAdrNr' | 'rechnungsAdrNr',
        subRecord: 'lieferAdr' | 'rechnungsAdr',
        label: string,
        typ: ZusatzadresseTyp,
    ) => (
        <ZusatzadresseSelect
            label={label}
            kNr={auftrag?.kNr}
            zaNr={auftrag?.[column] || 0}
            typ={typ}
            zusatzadresse={auftrag?.[subRecord]}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newZaNr, newZusatzadresse): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        [column]: newZaNr || 0,
                        [subRecord]: newZusatzadresse,
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={auftrag?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={auftrag?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (auftrag) {
                    setAuftrag({
                        ...auftrag,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    const offenPositionenCount = (auftragPositionList.data || []).filter((ap) => ap.offen > 0).length;

    const trProzSum = (rechnungList.data || []).reduce((acc, rechnung) => acc + rechnung.trProz, 0);
    const trNum = (rechnungList.data || []).reduce((acc, rechnung) => Math.max(acc, rechnung.teilR), 0);

    const validationWarning =
        auftrag?.ara !== 1 && // "Die Ware ist nicht entpflichtet"
        auftragPositionList.data &&
        !auftragPositionList.data.find((ap) => ap.artikelGruppeNr === 97)
            ? 'Achtung: Es ist noch kein Reclay Artikel eingetragen.'
            : null;

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={getAuftragLabel(auftrag, kunde.data)}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>
                            <div className={formStyles.threeColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Auftrag-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('angebotNr', 'Angebot-Nr.', { maxLength: 10 })}
                                    {auftrag?.angebotNr ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('angebote', defaultKNr, auftrag?.angebotNr)}
                                        />
                                    ) : null}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('serviceNr', 'Service-Nr.', { maxLength: 10 })}
                                    {auftrag?.serviceNr ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('services', defaultKNr, auftrag?.serviceNr)}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('abrufZu', 'Abruf zu', {
                                        maxLength: 10,
                                        disabled: true, // auftrag?.rahmen,
                                    })}
                                    {auftrag?.abrufZu ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('auftraege', defaultKNr, auftrag?.abrufZu)}
                                            runBefore={() => {
                                                onClose(false);
                                            }}
                                        />
                                    ) : null}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderCheckbox('rahmen', 'Rahmenauftrag')}
                                    {renderCheckbox('betrUm', 'Betreuerumsatz')}
                                    {renderCheckbox('teilR', 'Teilrechnung')}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datBis', 'Auftrag gültig bis')}
                                </div>
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                                <div className={formStyles.gridCell}>{renderMa('telBetreuer', 'Tel.-Betreuer')}</div>
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    <div className={styles.linkBoxTitle}>Lieferungen</div>
                                    {(lieferungList.data || []).map((lieferung) => (
                                        <div>
                                            <OpenLink
                                                link={createRecordUrl('lieferungen', defaultKNr, lieferung?.nr)}
                                                label={
                                                    moment(lieferung.datum).format('DD.MM.YYYY') + ': ' + lieferung.nr
                                                }
                                                runBefore={() => {
                                                    onClose(false);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className={formStyles.gridCell}>
                                    <div className={styles.linkBoxTitle}>Bestellungen</div>
                                    {(bestellungList.data || []).map((bestellung) => (
                                        <div>
                                            <OpenLink
                                                link={createRecordUrl('bestellungen', defaultKNr, bestellung?.nr)}
                                                label={
                                                    moment(bestellung.datum).format('DD.MM.YYYY') + ': ' + bestellung.nr
                                                }
                                                runBefore={() => {
                                                    onClose(false);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    <div className={styles.linkBoxTitle}>Rechnungen</div>
                                    {(rechnungList.data || []).map((rechnung) => (
                                        <div>
                                            <OpenLink
                                                link={createRecordUrl('rechnungen', defaultKNr, rechnung?.nr)}
                                                label={
                                                    moment(rechnung.datum).format('DD.MM.YYYY') +
                                                    ': ' +
                                                    rechnung.nr +
                                                    (rechnung.trProz > 0 ? ' ' + rechnung.trProz + '%' : '')
                                                }
                                                runBefore={() => {
                                                    onClose(false);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {auftrag?.rahmen ? (
                                    <div className={formStyles.gridCell}>
                                        <div className={styles.linkBoxTitle}>Abrufaufträge</div>
                                        {(abrufAuftragList.data || []).map((abrufAuftrag) => (
                                            <div>
                                                <OpenLink
                                                    link={createRecordUrl('auftraege', defaultKNr, abrufAuftrag?.nr)}
                                                    label={
                                                        moment(abrufAuftrag.datum).format('DD.MM.YYYY') +
                                                        ': ' +
                                                        abrufAuftrag.nr
                                                    }
                                                    runBefore={() => {
                                                        onClose(false);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div />
                                )}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    <div className={styles.linkBoxTitle}>Services</div>
                                    {(serviceList.data || []).map((service) => (
                                        <div>
                                            <OpenLink
                                                link={createRecordUrl('services', defaultKNr, service?.nr)}
                                                label={moment(service.datum).format('DD.MM.YYYY') + ': ' + service.nr}
                                                runBefore={() => {
                                                    onClose(false);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && auNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.auftrag}
                                        smRef={auNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB1', 'Textbaustein 1', 22, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderZusatzadresse('lieferAdrNr', 'lieferAdr', 'Lieferadresse', 'L')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderAddKundeSelect('lieferKNr', 'lieferKunde', 'Lieferkunde')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderZusatzadresse('rechnungsAdrNr', 'rechnungsAdr', 'Rechnungsadresse', 'R')}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderAddKundeSelect('rechnungsKNr', 'rechnungsKunde', 'Rechnungskunde')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderAuftragStatus()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefZeit', 'Lieferzeit', 14, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderDatePicker('liefDat', 'Lieferdatum')}</div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('liefKw', 'Liefer-KW', { numeric: true })}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>{renderSkontodefinition()}</div>
                            <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('fracht', 'Fracht Lager', {}, true)}
                                </div>
                                <div className={formStyles.gridCell}>{renderDecimalField('tipp', 'Tipp-Prov.(%)')}</div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('roadPr', 'Fracht extern', {}, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('mwstCode', 'MwSt.', { fixed: 0, disabled: true })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                        <div className={formStyles.secondColumn}>{renderTextarea('zusatz', 'Zusatzvereinbarung')}</div>
                    </div>

                    <div>
                        {auftrag && auftragPositionList?.data && (
                            <AuftragPositionen
                                kunde={kunde.data || defaultKunde}
                                auftrag={auftrag}
                                auftragPositionen={auftragPositionList?.data}
                                wiedervorlagen={wiedervorlagen.data || []}
                                viewMode="long"
                                allowEdit={!readOnlyPositionen && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    auftragPositionList.refetch().then();
                                    wiedervorlagen.refetch().then();
                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {auftragPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(auftrag?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(auftrag?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(auftrag?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={auftrag?.anlageDat}
                        createdDecimalTime={auftrag?.anlageUhr}
                        createdUser={auftrag?.anlageUser}
                        modifiedDate={auftrag?.updDat}
                        modifiedDecimalTime={auftrag?.updUhr}
                        modifiedUser={auftrag?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {validationWarning && <div className={formStyles.warn}>{validationWarning}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="AB Senden"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr}
                        />
                        {auftrag?.rahmen && (
                            <DefaultButton
                                className={formStyles.migrateButton}
                                onClick={() => handleButtonAction('createAbrufAuftrag')}
                                text="Abrufauftrag erstellen"
                                disabled={
                                    savingState !== SavingState.notChanged || !auftrag?.nr || auftrag?.status === 4
                                }
                            />
                        )}
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createService')}
                            text="Service erstellen"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr || auftrag?.status === 4}
                        />
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createBestellung')}
                            text="Bestellung erstellen"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr || auftrag?.status === 4}
                        />
                        {auftrag?.rahmen || (
                            <DefaultButton
                                className={formStyles.migrateButton}
                                onClick={() => handleButtonAction('createLieferung')}
                                text="Lieferung erstellen"
                                disabled={
                                    savingState !== SavingState.notChanged ||
                                    !auftrag?.nr ||
                                    auftrag?.status === 4 ||
                                    offenPositionenCount === 0
                                }
                            />
                        )}
                        {auftrag?.rahmen || (
                            <DefaultButton
                                className={formStyles.migrateButton}
                                onClick={() => handleButtonAction('createRechnung')}
                                text="Rechnung erstellen"
                                disabled={
                                    savingState !== SavingState.notChanged || !auftrag?.nr || auftrag?.status === 4
                                }
                            />
                        )}
                        <DefaultButton
                            className={formStyles.finalizeButton}
                            onClick={() => handleButtonAction('finalize')}
                            text="Auftrag abschließen"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr || auftrag?.status === 4}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !auftrag?.nr}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbAuftrag.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbAuftrag.isLoading || dbAuftrag.isFetching || kunde.isLoading || kunde.isFetching || !auftrag) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || auNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={auftrag?.kNr}
                    onChange={async (newKNr) => {
                        if (auftrag && newKNr) {
                            const { newNr } = await copyAuftrag(newKNr, auftrag, auftragPositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={auftrag?.kNr || 0}
                    kaNr={auftrag?.kaNr}
                    markAsSentDisabled={auftrag?.status !== 0}
                    markAsSentLabel="Auftrag als versendet markieren"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent) => {
                        if (auftrag) {
                            sendAuftrag(auftrag, kaNrListTo, kaNrListCC).then(() => {
                                setShowSend(false);
                                if (markAsSent && auftrag) {
                                    save(undefined, 1);
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Auftrag löschen"
                    message={'Soll der Auftrag ' + auftrag?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (auftrag?.nr) {
                            deleteAuftrag(auftrag).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => {
                        setShowDelete(false);
                    }}
                />
            )}
            {showCreateAbrufAuftrag && (
                <ConfirmMessage
                    title="Abrufauftrag erstellen"
                    message={'Aus dem Rahmenauftrag ' + auftrag?.nr + ' wird ein Abrufauftrag erstellt.'}
                    minWidth={1000}
                    confirmText="Abrufauftrag erstellen"
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Ein Abrufauftrag mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden aus &apos;Offen&apos; übernommen.</li>
                                <li>Die bestätigten Mengen werden vom Rahmenauftrag abgezogen.</li>
                                <li>Der neue Abrufauftrag wird geöffnet.</li>
                                <li>
                                    {/* eslint-disable-next-line max-len */}
                                    Wenn Lieferart <strong>Reserviert</strong>/<strong>Reserviert für Rahmen</strong>{' '}
                                    gewählt wird, wird <strong>Reservierungen</strong> in der Lagerverwaltung erhöht.
                                </li>
                            </ul>
                            <KundeSelect
                                title="Kundenauswahl"
                                label="Kunde"
                                kNr={transferKundeNr || 0}
                                name={transferKunde?.name1}
                                onChange={(newKNr, newKunde) => {
                                    setTransferKundeNr(newKNr || null);
                                    setTransferKunde(newKunde || null);
                                }}
                            />
                            <AuftragPositionenSelect
                                auftragPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                                withBestand
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (auftrag?.nr) {
                            await addAuftragFromAuftrag(
                                { ...auftrag, kNr: transferKundeNr || auftrag.kNr || 0 },
                                transferPositionen || [],
                            ).then((result) => {
                                onClose(true);
                                history.push(createRecordUrl('auftraege', transferKundeNr || undefined, result.newNr));
                            });
                        }
                    }}
                    onCancel={() => {
                        setShowCreateAbrufAuftrag(false);
                        setTransferKundeNr(null);
                        setTransferKunde(null);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showCreateBestellung && (
                <ConfirmMessage
                    title="Bestellung erstellen"
                    message={'Aus dem Auftrag ' + auftrag?.nr + ' wird eine Bestellung erstellt.'}
                    minWidth={1000}
                    confirmText="Bestellung erstellen"
                    confirmDisabled={!transferKundeNr}
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Eine Bestellung mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden aus &apos;Offen&apos; übernommen.</li>
                                <li>Doppelte Artikel wurden zusammengefasst.</li>
                                <li>
                                    Wird als Lieferart <strong>Lager</strong> gewählt, wird{' '}
                                    <strong>Offene Bestellungen</strong> in der Lagerverwaltung erhöht.
                                </li>
                                <li>Die neue Bestellung wird geöffnet.</li>
                            </ul>
                            <KundeSelect
                                title="Lieferantenauswahl"
                                label="Lieferant"
                                kNr={transferKundeNr || 0}
                                name={transferKunde?.name1}
                                onChange={(newKNr, newKunde) => {
                                    setTransferKundeNr(newKNr || null);
                                    setTransferKunde(newKunde || null);
                                }}
                            />
                            <AuftragPositionenSelect
                                auftragPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                                withBestand
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (auftrag?.nr && transferKundeNr) {
                            await addBestellungFromAuftrag(
                                artikelCalculator,
                                transferKundeNr,
                                auftrag,
                                transferPositionen || [],
                            ).then((result) => {
                                onClose(true);
                                history.push(createRecordUrl('bestellungen', transferKundeNr || 0, result.newNr));
                            });
                        }
                    }}
                    onCancel={() => {
                        setShowCreateBestellung(false);
                        setTransferKundeNr(null);
                        setTransferKunde(null);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showCreateService && (
                <ConfirmMessage
                    title="Service erstellen"
                    message={'Aus dem Auftrag ' + auftrag?.nr + ' wird ein Service erstellt.'}
                    minWidth={1000}
                    confirmText="Service erstellen"
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Eine Service mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden aus &apos;Offen&apos; übernommen.</li>
                                <li>Doppelte Artikel wurden zusammengefasst.</li>
                                <li>Das neue Service wird geöffnet.</li>
                            </ul>
                            <AuftragPositionenSelect
                                auftragPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt={false}
                                withBestand={false}
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (auftrag?.nr) {
                            await addServiceFromAuftrag(artikelCalculator, auftrag, transferPositionen || []).then(
                                (result) => {
                                    onClose(true);
                                    history.push(createRecordUrl('services', auftrag.kNr, result.newNr));
                                },
                            );
                        }
                    }}
                    onCancel={() => {
                        setShowCreateService(false);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showCreateLieferung && (
                <ConfirmMessage
                    title="Lieferung erstellen"
                    message={'Aus dem Auftrag ' + auftrag?.nr + ' wird eine Lieferung erstellt.'}
                    minWidth={1000}
                    confirmText="Lieferung erstellen"
                    cancelText="Abbruch"
                    content={
                        <>
                            <ul>
                                <li>Eine Lieferung mit den gewählten Positionen und Mengen wird erstellt.</li>
                                <li>Die vorgeschlagenen Mengen wurden aus &apos;Offen&apos; übernommen.</li>
                                <li>Die bestätigten Mengen werden vom Auftrag abgezogen.</li>
                                <li>
                                    Bei Lieferart <strong>Lager</strong> wird <strong>Offene Lieferungen</strong> in der
                                    Lagerverwaltung erhöht und der Bestand reduziert.
                                </li>
                                <li>Der neue Lieferschein wird geöffnet.</li>
                                <li>
                                    Der Status des Auftrages wird auf <strong>Teillieferung</strong> bzw.{' '}
                                    <strong>geliefert</strong> gesetzt.
                                </li>
                            </ul>
                            <AuftragPositionenSelect
                                auftragPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                                withBestand
                            />
                            {createLieferungError && <div className={formStyles.error}>{createLieferungError}</div>}
                        </>
                    }
                    onConfirm={async () => {
                        if (auftrag?.nr && transferPositionen) {
                            let error: string | null = null;

                            transferPositionen.forEach((transferPosition) => {
                                if (transferPosition && transferPosition.transferMenge !== undefined) {
                                    if (transferPosition.transferMenge > transferPosition.offen) {
                                        error = 'Es darf nicht mehr geliefert werden, als am Auftrag offen ist.';
                                    } else if (
                                        transferPosition.transferLArt === 'L' &&
                                        transferPosition.transferBestand !== undefined &&
                                        transferPosition.transferMenge > transferPosition.transferBestand
                                    ) {
                                        error =
                                            // eslint-disable-next-line max-len
                                            'Es darf nicht mehr geliefert werden, als im Lager vorhanden ist (Bestand).';
                                    }
                                }
                            });

                            if (error) {
                                setCreateLieferungError(error);
                            } else {
                                await addLieferungFromAuftrag(auftrag, transferPositionen || []).then((result) => {
                                    history.push(createRecordUrl('lieferungen', result.kNr, result.newNr));
                                });
                            }
                        }
                    }}
                    onCancel={() => {
                        setShowCreateLieferung(false);
                        setTransferKundeNr(null);
                        setTransferKunde(null);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {showCreateRechnung && (
                <ConfirmMessage
                    title="Rechnung erstellen"
                    message={'Soll aus dem Auftrag ' + auftrag?.nr + ' eine Rechnung erstellt werden?'}
                    minWidth={1100}
                    content={
                        <>
                            <ul>
                                <li>Ein Rechnung mit sämtlichen Positionen aus dem Auftrag wird erstellt.</li>
                                <li>Die Auftragsnummer wird bei der Rechnung hinterlegt.</li>
                                <li>
                                    Der Status des Auftrages wird auf <strong>{AuftragStatusLabel[4]}</strong> gesetzt.
                                </li>
                                <li>Die neue Rechnung wird geöffnet.</li>
                            </ul>

                            <div>
                                <div>
                                    <strong>Gestellte Rechnungen:</strong>
                                </div>

                                {(rechnungList.data?.length || 0) === 0 ? (
                                    <div>keine</div>
                                ) : (
                                    <>
                                        <div className={styles.rechnungLine}>
                                            <div>
                                                <strong>Rechnung</strong>
                                            </div>
                                            <div>
                                                <strong>Datum</strong>
                                            </div>
                                            <div>
                                                <strong>Status</strong>
                                            </div>
                                            <div>
                                                <strong>Teilr.%</strong>
                                            </div>
                                            <div>
                                                <strong>Netto</strong>
                                            </div>
                                            <div>
                                                <strong>Brutto</strong>
                                            </div>
                                            <div />
                                        </div>
                                        {(rechnungList.data || [])
                                            .sort((a, b) => a.datum.localeCompare(b.datum))
                                            .map((rechnung) => (
                                                <>
                                                    <div className={styles.rechnungLine} key={rechnung.nr}>
                                                        <div>
                                                            <OpenLink
                                                                link={createRecordUrl(
                                                                    'rechnungen',
                                                                    rechnung.kNr,
                                                                    rechnung.nr,
                                                                )}
                                                                label={rechnung.nr}
                                                            />
                                                        </div>
                                                        <div>{moment(rechnung.datum).format('DD.MM.YYYY')}</div>
                                                        <div>
                                                            {RechnungStatusLabel[rechnung.status] || rechnung.status}
                                                        </div>
                                                        <div>{formatPercent(rechnung.trProz)}</div>
                                                        <div>{formatMoney(rechnung.sNetto)}</div>
                                                        <div>{formatMoney(rechnung.sBrutto)}</div>
                                                        <div>
                                                            {showTeilrechnungDetails[rechnung.nr] ? (
                                                                <ChevronUpIcon
                                                                    className={formStyles.positionenAction}
                                                                    onClick={() => {
                                                                        setShowTeilrechnungDetails({
                                                                            ...showTeilrechnungDetails,
                                                                            [rechnung.nr]: false,
                                                                        });
                                                                    }}
                                                                />
                                                            ) : (
                                                                <ChevronDownIcon
                                                                    className={formStyles.positionenAction}
                                                                    onClick={() => {
                                                                        setShowTeilrechnungDetails({
                                                                            ...showTeilrechnungDetails,
                                                                            [rechnung.nr]: true,
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {showTeilrechnungDetails[rechnung.nr] && (
                                                        <div className={styles.rechnungPositionList}>
                                                            <RechnungPositionen
                                                                kunde={defaultKunde}
                                                                rNr={rechnung.nr}
                                                                rechnungPositionen={(
                                                                    rechnungPositionList?.data || []
                                                                ).filter((p) => p.rNr === rechnung.nr)}
                                                                viewMode="short"
                                                                allowEdit={false}
                                                                withHeader
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            ))}
                                    </>
                                )}
                            </div>
                            <div className={styles.teilrechnung}>
                                <Checkbox
                                    className={styles.checkbox}
                                    label="Teilrechnung"
                                    checked={teilrechnung.isTeilRechnung}
                                    onChange={(event, checked) => {
                                        setTeilRechnung({
                                            ...teilrechnung,
                                            isTeilRechnung: !!checked,
                                        });
                                    }}
                                />
                                <FormDecimalField
                                    value={teilrechnung.trProz}
                                    savingState={savingState}
                                    options={{ disabled: !teilrechnung.isTeilRechnung }}
                                    onChange={(newValue): void => {
                                        setTeilRechnung({
                                            ...teilrechnung,
                                            trProz: newValue,
                                            trProzCalc: newValue === 100 ? 100 - trProzSum : newValue,
                                        });
                                    }}
                                    label=""
                                />
                                <div className={styles.percentSign}>%</div>
                            </div>
                            <div>
                                <Checkbox
                                    className={styles.checkbox}
                                    label="Auftrag abschließen"
                                    checked={finalizeAuftragWithRechnung}
                                    onChange={(event, checked) => {
                                        setfinalizeAuftragWithRechnung(!!checked);
                                        setSetOffenToZero(true);
                                    }}
                                />
                                <Checkbox
                                    className={styles.checkbox}
                                    disabled={!finalizeAuftragWithRechnung}
                                    label="Alle offenen Mengen auf 0 setzen"
                                    checked={finalizeAuftragWithRechnung && setOffenToZero}
                                    onChange={(event, checked) => {
                                        setSetOffenToZero(!!checked);
                                    }}
                                />
                            </div>

                            <AuftragPositionenSelect
                                auftragPositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt={false}
                                withBestand={false}
                                trProzCalc={teilrechnung.isTeilRechnung ? teilrechnung.trProzCalc : 0}
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (kunde.data && auftrag?.nr) {
                            let transferPositionen2 = transferPositionen;
                            if (teilrechnung.isTeilRechnung) {
                                transferPositionen2 = (transferPositionen || []).map((tp) => ({
                                    ...tp,
                                    posEkPreis: tp.posEkPreis * (teilrechnung.trProzCalc / 100),
                                    posEPreis: tp.posEPreis * (teilrechnung.trProzCalc / 100),
                                    posNetto: tp.posNetto * (teilrechnung.trProzCalc / 100),
                                    posBrutto: tp.posBrutto * (teilrechnung.trProzCalc / 100),
                                }));
                            }

                            await addRechnungFromAuftrag(
                                kunde.data,
                                auftrag,
                                transferPositionen2 || [],
                                teilrechnung.isTeilRechnung ? trNum + 1 : 0,
                                teilrechnung.isTeilRechnung ? teilrechnung.trProz : 0,
                            ).then((result) => {
                                save(async () => {
                                    if (finalizeAuftragWithRechnung) {
                                        await finalizeAuftrag(auftrag, setOffenToZero);
                                    }
                                    history.push(createRecordUrl('rechnungen', auftrag.kNr, result.newNr));
                                });
                            });
                        }
                    }}
                    onCancel={() => {
                        setShowCreateRechnung(false);
                        setTransferKundeNr(null);
                        setTransferKunde(null);
                        setTransferPositionen(null);
                        setSetOffenToZero(true);
                    }}
                />
            )}
            {showFinalize && (
                <ConfirmMessage
                    title="Auftrag abschließen"
                    message={'Soll der Auftrag ' + auftrag?.nr + ' abgeschlossen werden?'}
                    minWidth={550}
                    content={
                        <>
                            <ul>
                                <li>
                                    Der Status des Auftrages wird auf <strong>{LieferungStatusLabel[4]}</strong>{' '}
                                    gesetzt.
                                </li>
                            </ul>
                            <Checkbox
                                className={styles.checkbox}
                                label="Alle offenen Mengen auf 0 setzen."
                                checked={setOffenToZero}
                                onChange={(event, checked) => {
                                    setSetOffenToZero(!!checked);
                                }}
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (kunde && auftrag?.nr) {
                            await finalizeAuftrag(auftrag, setOffenToZero).then(() => {
                                onReopen(auftrag.nr);
                            });
                        }
                    }}
                    onCancel={() => setShowFinalize(false)}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Auftrag ' +
                        auftrag?.nr +
                        ' auf "' +
                        AuftragStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (auftrag?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && auNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.auftrag}
                    forcedSmRef={auNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
